// web/src/components/board/AddTaskModal.jsx
// Modal to add one task to the Daily Board. Owns its own mutation; invalidates
// ['tasks'] + ['dashboard'] on success, then hands the saved date back so the
// board can jump to it when you scheduled something for another day.
//
// Owner/manager only — that's what POST /tasks enforces, so the button that
// opens this is gated the same way rather than letting a trainer hit a 403.

import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import TimeSelect from '../ui/TimeSelect';
import { fmtDate } from '../../lib/dates';

// task_type is free text in the schema; these are the barn's everyday jobs,
// with "Something else" as the escape hatch so the list never blocks anyone.
const TASK_TYPES = [
  ['feed', 'Feed'],
  ['turnout', 'Turnout'],
  ['bring in', 'Bring in'],
  ['ride', 'Ride'],
  ['groom', 'Groom'],
  ['stall clean', 'Stall clean'],
  ['blanket', 'Blanket'],
  ['medicate', 'Medicate'],
  ['farrier', 'Farrier'],
  ['vet', 'Vet'],
  ['__other', 'Something else…'],
];

const RIDE_TYPES = [
  ['', '—'],
  ['flat', 'Flat'],
  ['jumping', 'Jumping'],
  ['dressage', 'Dressage'],
  ['hack', 'Hack'],
  ['lunge', 'Lunge'],
];

// Mirrors the board's own grouping (and the time_block CHECK constraint).
const TIME_BLOCKS = [
  ['am', 'Morning'],
  ['noon', 'Midday'],
  ['pm', 'Afternoon'],
  ['nc', 'Night check'],
  ['', 'Anytime'],
];

// Who can be handed a task. Clients/riders/providers aren't barn staff.
const ASSIGNABLE_ROLES = ['owner', 'manager', 'trainer', 'groom'];


export default function AddTaskModal({ date, onClose, onCreated }) {
  const qc = useQueryClient();
  const firstFieldRef = useRef(null);

  const [taskType, setTaskType] = useState('feed');
  const [customType, setCustomType] = useState('');
  const [rideType, setRideType] = useState('');
  const [horseId, setHorseId] = useState('');
  const [taskDate, setTaskDate] = useState(date ?? '');
  const [timeBlock, setTimeBlock] = useState('am');
  const [timed, setTimed] = useState(false);
  // "HH:MM" 24-hour, the way every other time in the app is held. This was
  // three pieces of state feeding three dropdowns and a local to24h that had
  // to be kept in step with the one in LessonEditor.
  const [time, setTime] = useState('08:00');
  const [assignedUserId, setAssignedUserId] = useState('');
  const [notes, setNotes] = useState('');

  // The board resolves "today" server-side, so `date` can arrive a tick late.
  useEffect(() => {
    if (date) setTaskDate((d) => d || date);
  }, [date]);

  useEffect(() => {
    firstFieldRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const horsesQ = useQuery({
    queryKey: ['horses'],
    queryFn: () => api.get('/horses'),
  });
  // Assigning is a nicety — if /team is unavailable the form still saves.
  const teamQ = useQuery({
    queryKey: ['team'],
    queryFn: () => api.get('/team'),
    retry: false,
  });

  const horses = horsesQ.data?.horses ?? [];
  const staff = (teamQ.data?.members ?? []).filter((m) => ASSIGNABLE_ROLES.includes(m.role));

  const isOther = taskType === '__other';
  const resolvedType = (isOther ? customType : taskType).trim();

  const save = useMutation({
    mutationFn: () =>
      api.post('/tasks', {
        taskType: resolvedType,
        rideType: resolvedType === 'ride' ? rideType || null : null,
        horseId: horseId || null,
        date: taskDate || null,
        timeBlock: timeBlock || null,
        timeSpecific: timed ? time : null,
        assignedUserId: assignedUserId || null,
        notes: notes.trim() || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['schedule'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      onCreated?.(taskDate);
      onClose();
    },
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/30 sm:p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-task-title"
        className="card w-full sm:max-w-md p-5 rounded-b-none sm:rounded-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4">
          <h2 id="add-task-title" className="text-lg font-semibold">Add Task</h2>
          <p className="text-sm text-ink-500">{formatDate(taskDate)}</p>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!save.isPending && resolvedType) save.mutate();
          }}
          className="space-y-4"
        >
          <Field label="Task" htmlFor="task-type">
            <select
              id="task-type"
              ref={firstFieldRef}
              className="input w-full"
              value={taskType}
              onChange={(e) => setTaskType(e.target.value)}
            >
              {TASK_TYPES.map(([v, lbl]) => (
                <option key={v} value={v}>{lbl}</option>
              ))}
            </select>
            {isOther && (
              <input
                className="input w-full mt-2"
                aria-label="Task name"
                placeholder="e.g. hand walk"
                value={customType}
                onChange={(e) => setCustomType(e.target.value)}
              />
            )}
          </Field>

          {resolvedType === 'ride' && (
            <Field label="Ride type" optional htmlFor="ride-type">
              <select
                id="ride-type"
                className="input w-full"
                value={rideType}
                onChange={(e) => setRideType(e.target.value)}
              >
                {RIDE_TYPES.map(([v, lbl]) => (
                  <option key={v} value={v}>{lbl}</option>
                ))}
              </select>
            </Field>
          )}

          <Field label="Horse" optional htmlFor="task-horse">
            <select
              id="task-horse"
              className="input w-full"
              value={horseId}
              onChange={(e) => setHorseId(e.target.value)}
            >
              <option value="">Barn-wide (no horse)</option>
              {horses.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.name}
                  {h.stall ? ` (stall ${h.stall})` : ''}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Date" htmlFor="task-date">
            <input
              id="task-date"
              type="date"
              className="input w-full"
              value={taskDate}
              onChange={(e) => setTaskDate(e.target.value)}
            />
          </Field>

          <Field label="When" htmlFor="task-block">
            <select
              id="task-block"
              className="input w-full"
              value={timeBlock}
              onChange={(e) => setTimeBlock(e.target.value)}
            >
              {TIME_BLOCKS.map(([v, lbl]) => (
                <option key={v} value={v}>{lbl}</option>
              ))}
            </select>

            <label className="mt-2 inline-flex items-center gap-2 text-sm text-ink-500">
              <input type="checkbox" checked={timed} onChange={(e) => setTimed(e.target.checked)} />
              Set a specific time
            </label>

            {timed && (
              <div className="mt-2">
                <TimeSelect label="Time" value={time} onChange={setTime} />
              </div>
            )}
          </Field>

          <Field label="Assign to" optional htmlFor="task-assignee">
            <select
              id="task-assignee"
              className="input w-full"
              value={assignedUserId}
              onChange={(e) => setAssignedUserId(e.target.value)}
            >
              <option value="">Anyone</option>
              {staff.map((m) => (
                <option key={m.userId} value={m.userId}>
                  {m.name || m.email}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Notes" optional htmlFor="task-notes">
            <textarea
              id="task-notes"
              rows={2}
              className="input w-full"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Field>

          {save.isError && (
            <p className="text-sm text-red-600">
              {save.error?.status === 403
                ? 'Only an owner or manager can add tasks.'
                : 'Couldn’t save the task. Try again.'}
            </p>
          )}

          <div className="flex items-center gap-3 pt-1">
            <button type="submit" className="btn-primary" disabled={save.isPending || !resolvedType}>
              {save.isPending ? 'Adding…' : 'Add Task'}
            </button>
            <button type="button" onClick={onClose} className="text-sm text-ink-500 hover:text-ink-800">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, htmlFor, optional, children }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-sm font-medium text-ink-700 mb-1">
        {label}
        {optional && <span className="font-normal text-ink-400"> (optional)</span>}
      </label>
      {children}
    </div>
  );
}


function formatDate(iso) {
  if (!iso) return '';
  return fmtDate(iso, 'dayMedium');
}
